import { useCallback, useMemo } from "react";
import moment from "moment";
import { useUserState } from "../state/user";
import { agreeOnPaymentTerms } from "../services/user";
import { firstEmail } from "../utils/email";

export interface ProfessionalAccountStatus {
  /** Identifies the professional on the payments API — sent as `issuer`. */
  paymentId?: string;
  /** The professional's plan — sent as `skuId` on configs and `planId` on transactions. */
  skuId?: string;
  /** Occupation code, compare against OCCUPATION in src/constants.ts. */
  occupation?: number;
  givenName?: string;
  familyName?: string;
  email?: string;
  /** When the professional last agreed to the payment terms, if ever. */
  agreedOnPaymentTerms?: string;
  /** When the payment terms were last revised. */
  latestPaymentTerms?: string;
  /** Whether the payment terms are still outstanding, for either reason below. */
  mustAgreeToPaymentTerms: boolean;
  /**
   * The terms were agreed to once and have been revised since.
   *
   * Told apart from never having agreed because the two call for different
   * treatment: someone who has never agreed simply has not started, and meets
   * the terms as the first step of registering an account; someone already
   * using the service has to reconfirm before carrying on, so the payments area
   * stays blocked until they do.
   */
  paymentTermsRevised: boolean;
  acceptPaymentTerms: () => Promise<void>;
  loading: boolean;
}

/**
 * Single source of truth for the professional data the payments feature needs.
 *
 * Everything is read from GET /api/professionals, whose `accountStatus` carries
 * both payment-terms dates; accepting them is PATCH /api/professionals/payment-terms,
 * which stamps `agreedOnPaymentTerms` with the server's clock and takes no body.
 */
export default function useProfessionalAccountStatus(): ProfessionalAccountStatus {
  const { user, token, loading, refreshUser } = useUserState();

  const agreedOnPaymentTerms = user?.accountStatus?.agreedOnPaymentTerms;
  const latestPaymentTerms = user?.accountStatus?.latestPaymentTerms;

  const acceptPaymentTerms = useCallback(async () => {
    await agreeOnPaymentTerms(token);
    // Pull the stamped date back into the user state so the gate closes.
    await refreshUser();
  }, [token, refreshUser]);

  return useMemo(() => {
    // Same rule as the mobile app (ListConfigsScreen): never agreed, or agreed
    // before the terms were last revised. Signup writes an empty string, so
    // everyone who has only ever used the web app lands in the first branch.
    //
    // The one deviation: mobile treats a missing `latestPaymentTerms` as "now",
    // which would re-open the modal immediately after accepting. Here a missing
    // revision date means there is nothing to agree to — a locked-out user with
    // no way through is the worse failure.
    const agreedOn = agreedOnPaymentTerms ? moment(agreedOnPaymentTerms) : null;
    const latest = latestPaymentTerms ? moment(latestPaymentTerms) : null;
    const mustAgreeToPaymentTerms = !agreedOn
      ? true
      : agreedOn.isValid() && latest?.isValid()
      ? agreedOn.isSameOrBefore(latest)
      : false;

    // Agreeing is what writes the date, so its presence is what separates
    // "never started" from "has to reconfirm".
    const paymentTermsRevised =
      mustAgreeToPaymentTerms && Boolean(agreedOnPaymentTerms);

    return {
      paymentId: user?.accountStatus?.paymentId,
      skuId: user?.accountStatus?.planName,
      occupation: user?.occupation,
      givenName: user?.personalInfo?.givenName,
      familyName: user?.personalInfo?.familyName,
      // personalInfo.emails comes in several shapes, so reuse the shared
      // extractor. It always returns an array, but that array is empty for a
      // professional with no address on file — the annotation is what stops the
      // index access from being typed as a plain string and misleading callers.
      email: firstEmail(user),

      agreedOnPaymentTerms,
      latestPaymentTerms,
      mustAgreeToPaymentTerms,
      paymentTermsRevised,
      acceptPaymentTerms,

      loading
    };
  }, [
    user,
    loading,
    agreedOnPaymentTerms,
    latestPaymentTerms,
    acceptPaymentTerms
  ]);
}
